import React from 'react';
import { Box, Typography } from '@mui/material';
import { Cpu, MemoryStick, HardDrive, Layers } from 'lucide-react';
import GlassPaper from '../../GlassPaper';
import MetricGauge from './MetricGauge';
import StatPill from './StatPill';

function fmtBytes(bytes) {
    if (!bytes || !Number.isFinite(bytes)) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let i = 0, v = bytes;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

export default function ClusterResourcesCard({ cluster }) {
    if (!cluster) return null;
    const { cpu, memory, storage, nodeCount, nodesOnline } = cluster;
    const allOnline = nodesOnline === nodeCount;

    return (
        <GlassPaper sx={{
            p: { xs: 1.75, sm: 2.25 },
            background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(59,130,246,0.08) 55%, rgba(168,85,247,0.08))',
            position: 'relative',
            overflow: 'hidden',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box sx={{
                        width: 36, height: 36, borderRadius: 2,
                        background: 'linear-gradient(135deg, rgba(99,102,241,0.28), rgba(59,130,246,0.22))',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#a5b4fc',
                    }}><Layers size={18} /></Box>
                    <Box>
                        <Typography sx={{ fontSize: 10.5, letterSpacing: 1.1, opacity: 0.7, textTransform: 'uppercase', fontWeight: 600 }}>
                            Datacenter · Cluster Resources
                        </Typography>
                        <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 700, lineHeight: 1.15 }}>
                            {nodesOnline} / {nodeCount} nodes online
                        </Typography>
                    </Box>
                </Box>
                <StatPill tone={allOnline ? 'good' : 'warn'}>
                    {allOnline ? 'Healthy' : 'Degraded'}
                </StatPill>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(3, 1fr)' },
                gap: { xs: 1, sm: 1.5 },
                alignItems: 'center',
            }}>
                <ResourceStat
                    icon={<Cpu size={13} />}
                    label="CPU"
                    pct={cpu?.pct}
                    detail={cpu?.cores ? `${cpu.cores} cores` : '—'}
                />
                <ResourceStat
                    icon={<MemoryStick size={13} />}
                    label="Memory"
                    pct={memory?.pct}
                    detail={`${fmtBytes(memory?.usedBytes)} / ${fmtBytes(memory?.totalBytes)}`}
                />
                <ResourceStat
                    icon={<HardDrive size={13} />}
                    label="Storage"
                    pct={storage?.pct}
                    detail={`${fmtBytes(storage?.usedBytes)} / ${fmtBytes(storage?.totalBytes)}`}
                />
            </Box>
        </GlassPaper>
    );
}

function ResourceStat({ icon, label, pct, detail }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <MetricGauge value={pct} label={label} size={90} stroke={9} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.75, mt: 0.25 }}>
                <Box sx={{ display: 'flex', color: '#94a3b8' }}>{icon}</Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {detail}
                </Typography>
            </Box>
        </Box>
    );
}
